import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

interface AgeGateProps {
  onVerify: (verified: boolean) => void;
}

export const AgeGate: React.FC<AgeGateProps> = ({ onVerify }) => {
  const [birthDate, setBirthDate] = useState({ month: '', day: '', year: '' });
  const [error, setError] = useState<string | null>(null);

  const calculateAge = (birthDate: Date) => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const { month, day, year } = birthDate;

    if (!month || !day || !year) {
      setError("Please enter your full date of birth.");
      return;
    }

    const date = new Date(`${year}-${month}-${day}`);

    // Check for invalid date
    if (isNaN(date.getTime())) {
      setError("Please enter a valid date.");
      return;
    }

    const age = calculateAge(date);

    if (age >= 21) {
      onVerify(true);
    } else {
      setError("You must be 21 or older to enter.");
      // Optional: Delay showing the exit/failure to prevent brute force retries immediately
      setTimeout(() => onVerify(false), 2000);
    }
  };

  const handleInputChange = (field: 'month' | 'day' | 'year', value: string) => {
    // Basic validation to allow only numbers
    if (value && !/^\d+$/.test(value)) return;
    setBirthDate(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md">
      <div className="bg-brand-950 border border-brand-800 p-8 rounded-2xl max-w-md w-full text-center shadow-2xl relative overflow-hidden">

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold-500 to-transparent opacity-50"></div>

        <ShieldCheck className="w-16 h-16 mx-auto text-gold-600 mb-6" />

        <h2 className="text-3xl font-serif text-brand-50 mb-2">Age Verification</h2>
        <p className="text-brand-300 mb-8 leading-relaxed text-sm">
          To access Aficionado, please enter your date of birth.
          <br />You must be 21 years of age or older.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex gap-2 justify-center">
            <div className="flex flex-col w-20">
              <label className="text-xs text-brand-400 mb-1 uppercase tracking-wider">Month</label>
              <input
                type="text"
                placeholder="MM"
                maxLength={2}
                className="bg-brand-900 border border-brand-700 rounded-lg p-3 text-center text-brand-50 focus:border-gold-500 focus:outline-none placeholder-brand-700"
                value={birthDate.month}
                onChange={(e) => handleInputChange('month', e.target.value)}
              />
            </div>
            <div className="flex flex-col w-20">
              <label className="text-xs text-brand-400 mb-1 uppercase tracking-wider">Day</label>
              <input
                type="text"
                placeholder="DD"
                maxLength={2}
                className="bg-brand-900 border border-brand-700 rounded-lg p-3 text-center text-brand-50 focus:border-gold-500 focus:outline-none placeholder-brand-700"
                value={birthDate.day}
                onChange={(e) => handleInputChange('day', e.target.value)}
              />
            </div>
            <div className="flex flex-col w-24">
              <label className="text-xs text-brand-400 mb-1 uppercase tracking-wider">Year</label>
              <input
                type="text"
                placeholder="YYYY"
                maxLength={4}
                className="bg-brand-900 border border-brand-700 rounded-lg p-3 text-center text-brand-50 focus:border-gold-500 focus:outline-none placeholder-brand-700"
                value={birthDate.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-500 bg-red-500/10 p-3 rounded-lg text-sm justify-center border border-red-500/20">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="w-full py-3 px-6 bg-gradient-to-r from-brand-700 to-brand-800 hover:from-brand-600 hover:to-brand-700 text-gold-50 font-bold rounded-lg transition-all shadow-lg border border-brand-600 uppercase tracking-wide text-sm"
          >
            Enter Club
          </button>
        </form>

        <p className="mt-8 text-[10px] text-brand-500 uppercase tracking-widest opacity-60">
          Drink & Smoke Responsibly
        </p>
      </div>
    </div>
  );
};